import { fetchRestData } from '@/services/ApiRest';
import { AGENT_API_URL } from '@/services/agent/config';
import useAgentChatStore, { AgentConversation, AgentMessage } from '@/stores/useAgentChatStore';
import { useQuery } from '@tanstack/react-query';

export const agentKeys = {
  conversations: ['agent', 'conversations'] as const,
  conversationDetail: (id: string) => ['agent', 'conversations', id] as const,
};

export function useConversations(enabled = true) {
  return useQuery({
    queryKey: agentKeys.conversations,
    queryFn: async () => {
      const data = await fetchRestData<AgentConversation[]>(`${AGENT_API_URL}/conversations`);
      return data ?? [];
    },
    staleTime: 1000 * 60 * 5,
    enabled,
  });
}

export function useConversationMessages(conversationId: string | null) {
  return useQuery({
    queryKey: agentKeys.conversationDetail(conversationId || ''),
    queryFn: async () => {
      if (!conversationId) return { conversation: null, messages: [] };
      const data = await fetchRestData<{
        conversation: AgentConversation;
        messages: AgentMessage[];
      }>(`${AGENT_API_URL}/conversations/${conversationId}`);
      return data ?? { conversation: null, messages: [] };
    },
    enabled: !!conversationId,
    staleTime: 1000 * 60 * 5,
  });
}

// --- API Token hooks ---

export interface ApiToken {
  id: string;
  name: string;
  token_prefix: string;
  scopes: string[];
  is_active: boolean;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface CreateTokenResponse extends ApiToken {
  token: string; // plaintext — shown once only
}

export const tokenKeys = {
  list: ['agent', 'tokens'] as const,
};

export function useApiTokens(enabled = true) {
  return useQuery({
    queryKey: tokenKeys.list,
    queryFn: async () => {
      const data = await fetchRestData<ApiToken[]>(`${AGENT_API_URL}/tokens`);
      return data ?? [];
    },
    staleTime: 1000 * 60 * 5,
    enabled,
  });
}

export async function createApiToken(name: string, scopes: string[]): Promise<CreateTokenResponse | null> {
  return fetchRestData<CreateTokenResponse>(`${AGENT_API_URL}/tokens`, {
    method: 'POST',
    data: { name, scopes },
  });
}

export async function revokeApiToken(tokenId: string) {
  return fetchRestData(`${AGENT_API_URL}/tokens/${tokenId}`, {
    method: 'DELETE',
  });
}

// --- Conversation hooks ---

export async function deleteConversation(conversationId: string) {
  return fetchRestData(`${AGENT_API_URL}/conversations/${conversationId}/delete`, {
    method: 'DELETE',
  });
}

export async function loadConversation(conversationId: string) {
  const data = await fetchRestData<{
    conversation: AgentConversation;
    messages: Array<{
      id: string;
      role: 'user' | 'assistant';
      content: string;
      affected_entities: string[] | null;
      created_at: string;
    }>;
  }>(`${AGENT_API_URL}/conversations/${conversationId}`);

  if (data) {
    const store = useAgentChatStore.getState();
    store.setConversationId(conversationId);
    store.clearMessages();
    for (const msg of data.messages) {
      store.addMessage({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        affectedEntities: msg.affected_entities ?? undefined,
        createdAt: msg.created_at,
      });
    }
  }
}
